<template>
  <!--
    Deposits page facilitiates the process of making a deposit into one more more accounts.
    Steps are:
     - Add a credit card if one is not present
     - Indicate the amounts to deposit per spending account (authorized person)
     - Confirm amounts. Select 'end of session' handling
     - Display success message
  -->
  <mdb-container>
    <!-- Step #0: Add a credit card (if not present) -->
    <account-payment-method-new v-if="activeSection === '#add-card'" @next="goFormStep()" />
    <!-- Step #1: Enter the deposits into the form -->
    <section
      v-if="activeSection === '#deposits-form'"
      class="text-center mt-5 mb-1 mx-auto"
      style="max-width: 500px"
    >
      <h2 class="h1-responsive font-weight-bold text-left mt-5 mb-5">{{ $tc('nav.bank.deposits') }}</h2>
      <mdb-container class>
        <mdb-row
          v-for="(person, index) in persons"
          v-bind:key="index"
          :style="rowStyle(index)"
          class="pb-1 pt-3"
        >
          <mdb-col class="align-middle text-center">
            <mdb-row>
              <mdb-col class="px-0 py-0 mt-0">
                <b-img :src="person.imageUrl" rounded center height="70" :alt="$tc('phrase.photo')" />
              </mdb-col>
            </mdb-row>
            <mdb-row>
              <mdb-col>{{ person.firstName }}</mdb-col>
            </mdb-row>
          </mdb-col>
          <mdb-col class="my-auto">
            <mdb-input
              class="my-0 text-left"
              :name="'deposit-'+person.userId"
              :icon="$tc('icon.currency')"
              type="number"
              :label="$tc('phrase.deposit')"
              placeholder="0.00"
              v-model="person.deposit"
              @change="calcTotalDeposit()"
            />
          </mdb-col>
        </mdb-row>
        <mdb-row class="py-3 my-2" style="background: #fff1c7">
          <mdb-col class="text-right">{{ $tc('phrase.total') }}:</mdb-col>
          <mdb-col class="text-left">{{ $n(totalDeposit, 'currency') }}</mdb-col>
        </mdb-row>
      </mdb-container>
      <mdb-container class>
        <mdb-row>
          <mdb-col>
            <template v-if="MQ.mdOrBigger">
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                v-if="showSkipBtn"
                @click="handleSkipClick()"
              >{{ $tc('Skip') }}</mdb-btn>
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                v-else
                @click="goBankPage()"
              >{{ $tc('phrase.goto.my_accounts') }}</mdb-btn>
            </template>
            <mdb-btn
              class="mx-3 mt-3"
              :color="navStore.buttonNextColor"
              @click="handleNextClick()"
              :disabled="isDepositButtonDisabled"
            >{{ $tc('phrase.next') }}</mdb-btn>
          </mdb-col>
        </mdb-row>
      </mdb-container>
    </section>
    <!-- Step #2: Confirm the deposits into the form -->
    <section
      v-if="activeSection === '#deposits-confirm'"
      class="text-center mt-5 mb-1 mx-auto"
      style="max-width: 500px"
    >
      <h2
        class="h1-responsive font-weight-bold text-left mt-5 mb-3"
      >Confirm {{ $tc('nav.bank.deposits') }}</h2>
      <mdb-container class>
        <mdb-row class="py-3 my-2" style="background: #fff1c7">
          <mdb-col class="text-right" cols="2">{{ $tc('phrase.total') }}:</mdb-col>
          <mdb-col class="text-left">{{ $n(totalDeposit, 'currency') }}</mdb-col>
        </mdb-row>
        <mdb-row class="py-3 my-2" style="background: #fff1c7">
          <mdb-col class="text-right" cols="2">{{ $tc('phrase.card_credit') }}:</mdb-col>
          <mdb-col class="text-left">{{cardString}}</mdb-col>
        </mdb-row>
        <mdb-row v-if="showDonateOptions()" class="pt-3 mt-2" style="background: #fff1c7">
          <mdb-col class="text-left">{{ $tc('phrase.end_of_session_handling') }}:</mdb-col>
        </mdb-row>
        <mdb-row v-if="showDonateOptions()" class="pb-3" style="background: #fff1c7">
          <mdb-col class="text-left pl-3">
            <b-form-radio-group label="End of Session:">
              <b-form-radio
                v-model="endOfSession"
                v-for="(opt, i) of refundDonateOptions"
                :key="i"
                name="Session End Processing"
                :value="opt.value"
              >{{opt.text}}</b-form-radio>
              <!-- <b-form-radio v-model="endOfSession" name="Session End Processing" value="donate">Donate remaining to Campership Fund</b-form-radio>
              <b-form-radio v-model="endOfSession" name="Session End Processing" value="auto-refund">Refund remaining above ${{ refundThreshold }}</b-form-radio>-->
            </b-form-radio-group>
          </mdb-col>
        </mdb-row>
        <mdb-row v-if="getOrganization().showEndSessionMessage" class="py-3 my-2">
          <mdb-col class="text-left pl-3">{{ getOrganization().endSessionMessage }}</mdb-col>
        </mdb-row>
      </mdb-container>
      <mdb-container v-show="errorMessage != null">
        <mdb-row>
          <mdb-col>
            <b-alert variant="danger">{{ errorMessage }}</b-alert>
          </mdb-col>
        </mdb-row>
      </mdb-container>
      <mdb-container class>
        <mdb-row>
          <mdb-col>
            <template v-if="MQ.mdOrBigger && !isProcessing">
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                v-if="showSkipBtn"
                @click="handleSkipClick()"
              >{{ $tc('Skip') }}</mdb-btn>
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                v-else
                @click="goBankPage()"
              >{{ $tc('phrase.goto.my_accounts') }}</mdb-btn>
            </template>
            <mdb-btn
              class="mx-3 mt-3"
              :color="navStore.buttonNextColor"
              @click="handleVerifyClick()"
              :disabled="isVerifyButtonDisabled"
            >
              <b-spinner small v-if="isProcessing" class="mx-2" />
              <span v-if="!isProcessing">{{ $tc('phrase.confirm_deposit') }}</span>
              <span v-else>{{ $tc('phrase.depositing') }}...</span>
            </mdb-btn>
          </mdb-col>
        </mdb-row>
      </mdb-container>
    </section>
    <!-- Step #3: Succcess (Maybe) -->
    <section
      v-if="activeSection === '#deposits-result'"
      class="text-center mt-5 mb-1 mx-auto"
      style="max-width: 500px"
    >
      <h2
        class="h1-responsive font-weight-bold text-left mt-5 mb-3"
      >{{ (successMessage != null ? $tc('phrase.success') : $tc('phrase.success_partial')) }}</h2>
      <mdb-container>
        <mdb-row v-show="successMessage">
          <mdb-col>
            <b-alert variant="success">{{ successMessage }}</b-alert>
          </mdb-col>
        </mdb-row>
        <mdb-row v-show="errorMessage">
          <mdb-col>
            <b-alert variant="danger">{{ errorMessage }}</b-alert>
          </mdb-col>
        </mdb-row>
        <mdb-row class="py-3 my-2" style="background: #fff1c7">
          <mdb-col class="text-right" cols="2">{{ $tc('phrase.total') }}:</mdb-col>
          <mdb-col class="text-left">{{ $n(totalDeposit, 'currency') }}</mdb-col>
        </mdb-row>
        <mdb-row class="py-3 my-2" style="background: #fff1c7">
          <mdb-col class="text-right" cols="2">{{ $tc('phrase.card_credit') }}:</mdb-col>
          <mdb-col class="text-left">{{cardString}}</mdb-col>
        </mdb-row>
        <mdb-row>
          <mdb-col>
            <template v-if="MQ.smOrSmaller">
              <mdb-btn
                class="mx-3 mt-3"
                :color="navStore.buttonNextColor"
                @click="handleDoneClick()"
              >{{ $tc('phrase.done') }}</mdb-btn>
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                @click="goFormStep()"
                v-if="!showSkipBtn"
              >{{ $tc('phrase.make_another') }}</mdb-btn>
            </template>
            <template v-if="MQ.mdOrBigger">
              <mdb-btn
                class="mx-3 mt-3"
                color="light"
                @click="goFormStep()"
              >{{ $tc('phrase.make_another') }}</mdb-btn>
              <mdb-btn
                class="mx-3 mt-3"
                :color="navStore.buttonNextColor"
                @click="handleDoneClick()"
                v-if="!showSkipBtn"
              >{{ $tc('phrase.done') }}</mdb-btn>
            </template>
          </mdb-col>
        </mdb-row>
      </mdb-container>
    </section>
  </mdb-container>
</template>
<script>
import AccountPaymentMethodNew from "@/views/account/paymentmethods/new/AddMethodComponent";
import { mapGetters, mapMutations, mapActions } from "vuex";
import { mdbBtn, mdbCol, mdbContainer, mdbInput, mdbRow } from "mdbvue";
import { MediaQuery as MQ } from '@/MediaQuery';
import { RouterIcons } from '@/router/RouterIcons';
import { RouterUtils } from '@/router/RouterUtils';

export default {
  name: "MakeDeposit",
  props: {
    showSkipBtn: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AccountPaymentMethodNew,
    mdbBtn,
    mdbCol,
    mdbContainer,
    mdbInput,
    mdbRow
  },
  data() {
    return {
      activeSection: "#deposits-form",
      errorMessage: null,
      successMessage: null,
      RouterIcons: RouterIcons,
      persons: [],
      deposits: {},
      endOfSession: null,
      MQ: MQ,
      totalDeposit: "0.00",
      fromAccount: null,
      toAccount: null,
      refundThreshold: this.getOrganization().refundThreshold.toFixed(2),
      hasCard: false,
      cardString: this.$tc("phrase.card_not_on_file"),
      isProcessing: false,
      donateSelection: "",
      refundDonateOptions: []
    };
  },
  computed: {
    isVerifyButtonDisabled() {
      if (
        // !this.hasCard ||
        (this.showDonateOptions() &&
          (this.endOfSession == null || this.endOfSession === "")) ||
        this.isProcessing
      ) {
        return true;
      } else {
        return false;
      }
    },
    isDepositButtonDisabled() {
      if (parseFloat(this.totalDeposit) <= 0) {
        return true;
      }
      return false;
    },
    // methodOptions () {
    //   let options = []
    //   for (let index in this.getPaymentMethods()) {
    //     let method = this.getPaymentMethods()[index]
    //     // let option = { text: this.$tc('phrase.select_account'), value: null, disabled: true, selected: true }
    //     options.push(options)
    //   }
    //   return options
    // },
    contributingTo() {
      return this.getContributingToActive();
    },
    authPersons() {
      return this.getAuthorizedActivePersons();
    },
    paymentMethods() {
      return this.getPaymentMethods();
    }
  },
  watch: {
    contributingTo(/* contributingTo, old */) {
      this.setup();
    },
    authPersons(/* authPersons, old */) {
      this.setup();
    },
    "$route.hash": function(/* oldVal, newVal */) {
      // console.log("deposits route", this.$route, oldVal, newVal);
      if (this.$route.hash === "" || this.$route.hash === "#deposits-form") {
        this.setup();
      } else {
        this.activeSection = this.$route.hash;
      }
    }
  },
  mixins: [RouterUtils],
  methods: {
    ...mapGetters("bank", [
      "getAuthorizedActivePersons",
      "getContributingToActive",
      "getOrganization",
      "showDonateOptions",
      "getDonateOptions"
    ]),
    ...mapActions("bank", [
      // 'allocateFunds',
      "depositFunds",
      "fetchAuthorizedUsers",
      "fetchUserTransactions"
    ]),
    ...mapMutations("nav", ["pushBackbarStack", "resetBackbarStack"]),
    ...mapGetters("paymentmethods", ["getPaymentMethods"]),
    ...mapActions("user", [
      // 'depositFundsSquare',
      "fetchAuthorizedPersons",
      "sendDepositConfirmation"
    ]),
    ...mapGetters("user", ["getUserId"]),
    calcTotalDeposit() {
      var total = 0.0;
      for (var index in this.persons) {
        let person = this.persons[index];
        if (
          person.deposit != null &&
          person.deposit !== "" &&
          !isNaN(person.deposit)
        ) {
          person.deposit = parseFloat(person.deposit).toFixed(2);
          total = total + parseFloat(person.deposit);
        }
      }
      this.totalDeposit = total.toFixed(2);
    },
    goFormStep() {
      this.setup();
      window.location.hash = "deposits-form";
      this.activeSection = "#deposits-form";
      this.totalDeposit = 0;
    },
    handleDoneClick() {
      this.$emit("next");
    },
    handleNextClick() {
      // add route to stack
      this.pushBackbarStack(this.$route);
      // add hash to URL
      window.location.hash = "deposits-confirm";
      this.activeSection = "#deposits-confirm";
    },
    handleSkipClick() {
      this.$emit("skip");
    },
    handleVerifyClick() {
      let deposits = this.persons
        .filter(person => person.deposit)
        .map(person => {
          let deposit = {
            deposit: parseFloat(person.deposit),
            authUserId: person.userId
          };
          return deposit;
        });
      // get end-of-session handling
      let endOfSessionVal = "";
      let donateSelectionVal = "";
      if (
        this.endOfSession &&
        this.endOfSession.refundDonate &&
        this.endOfSession.donateSelection
      ) {
        endOfSessionVal = this.endOfSession.refundDonate;
        donateSelectionVal = this.endOfSession.donateSelection;
      }
      // process deposit
      this.isProcessing = true;
      this.depositFunds({
        depositAmount: parseFloat(this.totalDeposit),
        endOfSession: endOfSessionVal,
        donateSelection: donateSelectionVal,
        deposits
      })
        /*
      this.depositFundsSquare({
        deposit_amount: parseFloat(this.totalDeposit),
        endOfSession: endOfSessionVal,
        donateSelection: donateSelectionVal
      })
        .then(res => {
          this.allocateFunds({
            deposits,
            endOfSession: endOfSessionVal,
            donateSelection: donateSelectionVal
          })
      */
        .then(async () => {
          // console.log("make deposit response ", res);
          this.isProcessing = false;
          this.errorMessage = null;
          this.successMessage = "The funds have been deposited!";
          this.activeSection = "#deposits-result";
          window.location.hash = "deposits-result";
          // refresh the data
          await this.fetchUserPairs();
          this.fetchAuthorizedPersons({
            authorizedUsers: this.getAuthorizedActivePersons()
          });
          // refresh transactions
          this.fetchUserTransactions({ userId: this.getUserId() });
        })
        .catch(err => {
          // if err == 1
          // console.log("Deposit funds square error", err);
          let errorMsg =
            "There was a problem encountered while making the deposit: " +
            err.message;
          this.successMessage = null;
          this.errorMessage = errorMsg;
          this.isProcessing = false;

          // if err == 2
          // console.log("Allocation err:", err);
          this.successMessage = null;
          this.errorMessage =
            "Deposit succeeded. However, there was an error allocating funds to the spending account: " +
            err.message;
          // TODO @frank. Now what? The money is unallocated. We need a way to handle ... or detect this to help the user
          this.activeSection = "#deposits-result";
          window.location.hash = "deposits-result";
          this.isProcessing = false;
        });
      /*
        })
        .catch(err => {
          // console.log('Deposit funds square error', err)
          let errorMsg =
            'There was a problem encountered while making the deposit: ' +
            err.message
          this.successMessage = null
          this.errorMessage = errorMsg
          this.isProcessing = false
        })
        */
      // complete payment

      // this.goNotImplemented()
      /*
      // add route to stack
      this.pushBackbarStack(this.$route)
      // add hash to URL
      window.location.hash = 'confirm'
      // change the section viewed
      this.activeSection = 'confirm'
      */
    },
    rowStyle(index) {
      if (index % 2 === 0) {
        return "background: #f0f0f0";
      }
      return "";
    },
    setup() {
      // TODO: This code is in three different places. COnsolidate to a common library
      // determine if payment method is set
      /*
      if (this.paymentMethods.length > 0) {
        this.hasCard = true
        let defaultCard = this.paymentMethods.find(card => card.isDefault)
        if (defaultCard) {
          this.cardString = `${defaultCard.type} *${defaultCard.last4}`
        } else {
          this.cardString = this.$tc('phrase.card_default_not_set')
          this.activeSection = '#add-card'
          // TODO handle this better
        }
      } else {
        this.cardString = this.$tc('phrase.card_not_on_file')
        this.activeSection = '#add-card'
      }
      */
      // default to form
      if (window.location.hash === "") {
        this.activeSection = "#deposits-form";
      }
      // ...
      this.persons = this.getAuthorizedActivePersons()
        .concat(this.getContributingToActive())
        .map(person => {
          person.deposit = null;
          return person;
        });
      this.refundDonateOptions = [];
      this.getDonateOptions().forEach(option => {
        let opt = {
          value: {
            refundDonate: "donate",
            donateSelection: option
          },
          text: `Donate remaining to ${option}`
        };
        this.refundDonateOptions.push(opt);
      });
      // build option for refund
      this.refundThreshold = 0;
      let textVal =
        "Refund remaining funds" +
        (this.refundThreshold > 0 ? ` above ${this.refundThreshold}` : "");
      this.refundDonateOptions.push({
        value: {
          refundDonate: "refund"
        },
        text: textVal
      });
    }
  },
  created() {},
  mounted() {
    this.setup();
  }
};
</script>
